const SCROLL_THRESHOLD = 12;
const SCROLL_COOLDOWN_MS = 150;
const HIDE_HYSTERESIS = 20;

class HeaderComponent extends HTMLElement {
  connectedCallback() {
    this.header = document.querySelector('[data-header]');
    this.headerWrapper = document.querySelector('.shopify-section-group-header-group');
    this.announcementBar = document.querySelector('.section-announcement-bar');
    this.headerNav = this.querySelector('header-collapsible-component');
    this.mobileNavToggle = this.querySelector('mobile-nav-toggle');
    this.search = this.querySelector('[data-header-search]');
    this.searchToggle = this.querySelector('search-toggle');
    this.popovers = this.querySelectorAll('[popover]');
    this.popoverTriggers = this.querySelectorAll('button[popovertarget]');
    this.submenuTriggers = this.querySelectorAll('[data-submenu-trigger]');
    this.submenuTargets = this.querySelectorAll('[data-submenu-target]');

    this._cartUpdatedHandler = () => this.closeAllPopovers();
    window.addEventListener('cart:updated', this._cartUpdatedHandler);

    this.initSubmenuTargets();
    this.initSubmenuTriggers();
    this.initPopoverTriggers();
    if (this.headerWrapper) this.initScroll();
  }

  disconnectedCallback() {
    if (this.boundHandleScroll) {
      window.removeEventListener('scroll', this.boundHandleScroll);
    }
    if (this.boundHandleResize) {
      window.removeEventListener('resize', this.boundHandleResize);
    }
    window.removeEventListener('cart:updated', this._cartUpdatedHandler);
  }

  initSubmenuTargets() {
    this.submenuTargets.forEach(target => {
      const targetUrl = target.getAttribute('data-submenu-target');
      if (!targetUrl) return;

      fetch(`${targetUrl}&section_id=theme-collection-products-best-selling`)
        .then(response => response.text())
        .then(text => {
          target.innerHTML = new DOMParser().parseFromString(text, 'text/html').querySelector('.shopify-section').innerHTML;
          window.initDialogs(target);
        })
        .catch(console.error);
    });
  }

  initSubmenuTriggers() {
    this.submenuTriggers.forEach(trigger => {
      ['click', 'mouseenter'].forEach(event => trigger.addEventListener(event, event => this.submenuTriggersHandler(event, trigger)));
    });
  }

  initPopoverTriggers() {
    const useHover = !window.interestForElementSupported && document.documentElement.classList.contains('no-touch');

    this.popoverTriggers.forEach(trigger => {
      if (useHover) {
        ['click', 'mouseenter'].forEach(event => trigger.addEventListener(event, event => this.openPopover(event, trigger)));
      }
      else {
        trigger.addEventListener('click', event => {
          const popover = document.getElementById(trigger.getAttribute('popovertarget'));
          if (popover?.matches(':popover-open') && trigger.dataset?.url && trigger.dataset.url !== '#') {
            event.preventDefault();
            event.stopPropagation();
            window.location.href = trigger.dataset.url;
          }
        });
      }
    });
  }

  initScroll() {
    this.isScrolling = false;
    this.lastScrollTop = 0;
    this.hideRevealCooldownUntil = 0;

    const announcementHeight = this.announcementBar?.offsetHeight || 0;
    this.scrollOffset = announcementHeight + this.offsetHeight;
    this.headerStart = announcementHeight;

    this.updateCSSVars();

    this.boundHandleResize = debounce(() => {
      this.updateCSSVars();
      this.handleScroll();
    }, 100);

    this.boundHandleScroll = () => {
      if (this.isScrolling) return;
      this.isScrolling = true;
      requestAnimationFrame(() => {
        this.handleScroll();
        this.isScrolling = false;
      });
    };

    window.addEventListener('resize', this.boundHandleResize);
    window.addEventListener('scroll', this.boundHandleScroll, { passive: true });
  }

  updateCSSVars() {
    document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
    if (this.announcementBar) {
      document.documentElement.style.setProperty('--announcement-bar-height', `${this.announcementBar.offsetHeight}px`);
    }
  }

  openPopover(event, trigger) {
    if (!trigger) return;
    this.activePopover = document.getElementById(trigger.getAttribute('popovertarget'));
    if (!this.activePopover) return;

    if (event.type === 'click' && this.activePopover.matches(':popover-open') && trigger.dataset?.url && trigger.dataset.url !== '#') {
      window.location.href = trigger.dataset.url;
      return;
    }

    setTimeout(() => this.activePopover.showPopover({ source: trigger }), 300);
    this.activePopover.addEventListener('mouseleave', () => this.closePopover());
  }

  closePopover() {
    if (this.activePopover) setTimeout(() => this.activePopover.hidePopover(), 300);
  }

  closeAllPopovers() {
    Array.from(this.popovers).find(popover => popover.matches(':popover-open'))?.hidePopover();
  }

  submenuTriggersHandler(event, trigger) {
    event?.preventDefault();
    event?.stopPropagation();

    const targetId = trigger.getAttribute('aria-controls');

    if (!targetId && trigger.getAttribute('data-submenu-trigger') === 'false') {
      this.showDefaultSubmenu();
      if (event.type === 'click' && trigger.href && trigger.href !== '#') {
        window.location.href = trigger.href;
      }
      return;
    }

    if (event.type === 'click' && this.activeSubmenuTrigger === trigger && trigger.dataset?.url && trigger.dataset.url !== '#') {
      window.location.href = trigger.dataset.url;
      return;
    }

    this.activeSubmenuTrigger = trigger;

    if (targetId) {
      this.submenuTriggers.forEach(submenuTrigger => submenuTrigger.setAttribute('aria-expanded', submenuTrigger === trigger));
      this.submenuTargets.forEach(target => {
        target.classList.toggle('invisible', target.id !== targetId);
        target.classList.toggle('opacity-0', target.id !== targetId);
      });
    }
  }

  showDefaultSubmenu() {
    this.submenuTargets.forEach(target => {
      const isDefault = target.id === 'submenu-default';
      target.classList.toggle('invisible', !isDefault);
      target.classList.toggle('opacity-0', !isDefault);
    });
  }

  handleScroll() {
    const scrollTop = window.scrollY;
    const scrollDelta = scrollTop - this.lastScrollTop;
    const now = Date.now();

    if (scrollTop <= this.headerStart) {
      this.revealTop();
    }
    else if (!document.body.classList.contains('desktop-submenu-is-open')) {
      const cooldownElapsed = now >= this.hideRevealCooldownUntil;
      const atBottom = scrollTop + window.innerHeight > document.body.clientHeight;
      const shouldHide = scrollDelta > 0 && scrollTop > this.scrollOffset + HIDE_HYSTERESIS;
      const shouldReveal = scrollDelta < 0 && scrollTop > this.headerStart;

      if (cooldownElapsed && Math.abs(scrollDelta) > SCROLL_THRESHOLD) {
        if (atBottom || shouldHide) {
          this.hide();
        }
        else if (shouldReveal) {
          this.reveal();
        }
        this.hideRevealCooldownUntil = now + SCROLL_COOLDOWN_MS;
      }
    }

    this.lastScrollTop = scrollTop;
  }

  hide() {
    document.body.classList.add('header-hidden', 'header-sticky');
    this.closeAll();
  }

  reveal() {
    document.body.classList.add('header-sticky');
    document.body.classList.remove('header-hidden');
  }

  revealTop() {
    document.body.classList.remove('header-hidden', 'header-sticky');
    this.closePopovers();
  }

  closeAll() {
    this.closePopovers();
    this.headerNav?.closeAll?.();
    this.mobileNavToggle?.remove();
    if (this.search?.classList.contains('header__search--is-active')) this.searchToggle?.remove();
  }

  closePopovers() {
    this.popovers?.forEach(popover => popover.matches?.(':popover-open') && popover.hidePopover());
    document.body.classList.remove('desktop-submenu-is-open');
    this.showDefaultSubmenu();
  }

  toggleHeaderClass(open = true) {
    document.body.classList.toggle('desktop-submenu-is-open', open);
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}